import React, { FC, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Select } from 'antd';
import { IRoute, IPageable, IUserFiltres } from 'models';
import { userRoleLabel } from 'models/user/userRoleLabels';
import { userStore } from 'store';
import strings from 'tools';
import {
    Button,
    EmptySearch,
    ErrorRequest,
    Header,
    HeaderActivity,
    Icons,
    Input,
    Layout,
} from 'components/layout';
import { UsersTable } from 'components/user';

import './users.scss';
import { useNavigate } from 'react-router-dom';

interface Props {
    navigation: IRoute[];
}

const SEARCH_DEBOUNCE_MS = 400;

const buildUsersFilter = (search: string, role?: string): IUserFiltres => {
    const next: IUserFiltres = role ? { role } : {};
    const trimmed = search.trim();
    if (trimmed) {
        next.q = trimmed;
    }
    return next;
};

const filtersEqual = (a: IUserFiltres, b: IUserFiltres): boolean =>
    JSON.stringify(a) === JSON.stringify(b);

const DEFAULT_PAGEABLE: IPageable = { page: 0, size: 10 };

const Users: FC<Props> = observer((props: Props) => {
    const { users, isLoadingUser, errorUser, successAdd, roles } = userStore;
    const { navigation } = props;
    const [isAddUser, setIsAddUser] = useState<boolean>(false);
    const [search, setSearch] = useState<string>(userStore.usersListSearch);
    const [pageable, setPageable] = useState<IPageable>(userStore.usersListPageable);
    const [filter, setFilter] = useState<IUserFiltres>(userStore.usersListFilter);
    const inputRef = useRef<HTMLInputElement>(null);
    const skipInitialSearchDebounce = useRef(userStore.usersListSearch === '');

    useEffect(() => {
        userStore.setUsersListUi(search, filter, pageable);
    }, [search, filter, pageable]);

    const navigate = useNavigate();

    const changePage = (pageNum: number) => {
        setPageable((prev) => ({ ...prev, page: pageNum }));
    };

    useEffect(() => {
        userStore.getUsers(pageable, filter);
    }, [pageable, filter]);

    useEffect(() => {
        if (isAddUser) {
            navigate('/user/new/');
        }
    }, [isAddUser]);

    const onDeleteUser = (id: string) => {
        userStore.deleteUser(id);
    };

    const onBlockUser = (id: string) => {
        userStore.blockUser(id);
    };

    const onUnblockUser = (id: string) => {
        userStore.unblockUser(id);
    };

    useEffect(() => {
        if (successAdd) {
            setIsAddUser(false);
            setPageable({ page: 0, size: 10 });
            userStore.setSuccessAdd(false);
        }
    }, [successAdd]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (skipInitialSearchDebounce.current && search === '') {
                skipInitialSearchDebounce.current = false;
                return;
            }
            skipInitialSearchDebounce.current = false;

            setFilter((prev) => {
                const next = buildUsersFilter(search, prev.role);
                if (filtersEqual(prev, next)) {
                    return prev;
                }
                setPageable(DEFAULT_PAGEABLE);
                return next;
            });
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [search]);

    const changeRole = (role?: string) => {
        setFilter((prev) => {
            const next = buildUsersFilter(search, role);
            return filtersEqual(prev, next) ? prev : next;
        });
        setPageable((prev) =>
            prev.page === DEFAULT_PAGEABLE.page && prev.size === DEFAULT_PAGEABLE.size
                ? prev
                : DEFAULT_PAGEABLE
        );
    };

    useEffect(() => {
        inputRef.current?.focus();
        userStore.getRoles();
    }, []);

    useEffect(
        () => () => {
            userStore.setUsers(null);
        },
        []
    );

    return (
        <Layout page="users" navigation={navigation} error={errorUser} errorScreen>
            <Header
                title="Пользователи"
                description={
                    users
                        ? `Всего ${users.totalElements} ${strings.ending(users.totalElements, [
                              'пользователь',
                              'пользователя',
                              'пользователей',
                          ])}`
                        : ''
                }
                activity={<HeaderActivity loading={isLoadingUser} initial={users === null} />}
            />
            <div className="page-users">
                <div className="users-top">
                    <div className="users-top-filters">
                        <Input
                            ref={inputRef}
                            className="users-search"
                            onChange={(text: string) => setSearch(text)}
                            value={search || ''}
                            type="search"
                            placeholder="Поиск по фамилии, имени, email, телефону"
                        />
                        {roles !== null && (
                            <Select
                                className="users-role-filter users-filter-select"
                                placeholder="Роль"
                                allowClear
                                value={filter.role}
                                onChange={changeRole}
                                onClear={() => changeRole(undefined)}
                                options={roles.map((role) => ({
                                    value: role.value,
                                    label: userRoleLabel(role.value),
                                }))}
                            />
                        )}
                    </div>
                    <Button onClick={() => setIsAddUser(true)} type="primary">
                        {Icons.plus()}Создать пользователя
                    </Button>
                </div>
                {(users?.totalElements > 0 || isLoadingUser) && (
                    <UsersTable
                        returnTo="/users"
                        loading={isLoadingUser}
                        onDeleteUser={onDeleteUser}
                        onBlockUser={onBlockUser}
                        onUnblockUser={onUnblockUser}
                        data={users?.content}
                        page={pageable.page}
                        total={users?.totalElements}
                        onChangePage={changePage}
                    />
                )}
                {!users?.totalElements && search.trim() && !isLoadingUser && <EmptySearch />}
                {!users && errorUser && (
                    <ErrorRequest onClick={() => userStore.getUsers(pageable, filter)} />
                )}
                {isAddUser && null}
            </div>
        </Layout>
    );
});

export default Users;
